import { meApi } from './me';
import type { components, operations, paths } from './schema';

import { adminConfigsEndpoints } from '@/features/admin-configs/endpoints';
import { adminConsentTypesEndpoints } from '@/features/admin-consent-types/endpoints';
import { adminFhirIntegrationsEndpoints } from '@/features/admin-fhir-integrations/endpoints';
import { fhirLauncherEndpoints } from '@/features/fhir-launcher/endpoints';
import { adminIdentifierTypesEndpoints } from '@/features/admin-identifier-types/endpoints';
import { adminQuestionnaireTemplatesEndpoints } from '@/features/admin-questionnaire-templates/endpoints';
import { browseQuestionnairesEndpoints } from '@/features/browse-questionnaires/endpoints';
import { adminSystemEndpoints } from '@/features/admin-system/api';
import { adminUsersEndpoints } from '@/features/admin-users/endpoints';
import { userProfileEndpoints } from '@/features/user-profile/endpoints';
import { familiesApi } from '@/features/families/api';
import { featureFlagsEndpoints } from '@/features/feature-flags/endpoints';
import { patientRecordApi } from '@/features/patient-record/api';
import { patientSearchApi } from '@/features/patient-search/api';
import { platformEndpoints } from '@/features/platform/endpoints';
import { staffEndpoints } from '@/features/staff/endpoints';

/**
 * Aggregator for the legacy `apiClient` surface.
 *
 * Endpoints + their hand-written DTOs now live alongside the feature that owns
 * them (`src/features/<feature>/api/` for patient-record, `endpoints.ts` for
 * the admin features). This file just merges those slices so the ~50 existing
 * `apiClient.foo()` / `import type { Foo } from '@/api/client'` call sites
 * keep compiling. New code should prefer the per-feature slice directly.
 *
 * Design choices (documented for PTV2-911):
 *   - `src/api/schema.ts` is generated and checked in. Regenerate with
 *     `pnpm run generate:api` whenever ai-hub ships a new contract. CI parity
 *     is enforced by running `pnpm typecheck` on every PR — if the committed
 *     schema drifts from the OpenAPI source, downstream mappers fail to
 *     compile.
 *   - Callers get typed path / response / request-body helpers here, but
 *     raw response shape is still snake_case. Use the explicit mappers in
 *     `src/api/mappers/` to produce camelCase view models. No generic deep
 *     transform — we want each rename to be reviewed.
 */

// Re-export the generated namespaces so mappers and hooks don't import from
// `./schema` directly (makes the generated file easier to swap later).
export type { components, operations, paths };

export type {
  OperationPath,
  OperationPaths,
  OperationResult,
  PageEnvelope,
  Schemas,
} from './shared';

export type { CurrentUserDto, PermissionDto } from './me';

// patient-record slice types
export type {
  AddCareTeamMemberBody,
  CareTeamMemberDto,
  CareTeamMemberRowDto,
  CarePathwayDto,
  CarePathwayStepDto,
  CarePathwayTemplateDto,
  CarePathwayTemplateStepDto,
  CreatePersonInput,
  CreatePersonSexCode,
  EthnicityDto,
  EthnicityTermDto,
  IdentifierInUseResponseDto,
  LiveDemographicsBody,
  LiveInterpreterRequired,
  ListTasksParams,
  PhysicalChartDto as PatientPhysicalChartDto,
  TaskDto,
  TaskPageDto,
  TaskPriorityCode,
  TaskStatusCode,
  TaskSummaryDto,
  TaskTypeCode,
  UpdateTaskInput as TasksUpdateInput,
  LiveFeatureCreateInput,
  LiveFeatureDate,
  LiveFeatureObserved,
  LiveFeatureQualifier,
  LiveFeatureQualifierGroup,
  LiveFeatureQualifierType,
  LiveFeatureSource,
  LiveFeatureType,
  LiveSexCode,
  LiveVariantPartialDate,
  LiveVariantSource,
  GeneticInfoDto,
  NoteInputDto,
  PersonDto,
  PersonMetadataWithCareTeamDto,
  PersonSummaryDto,
  TenantProgramDto,
  TenantUserDto,
} from '@/features/patient-record/api';

// patient-search slice types
export type {
  PersonPageResponseDto,
  PersonSummaryPageResponseDto,
  SearchPersonsParams,
} from '@/features/patient-search/api';

// families slice types
export type {
  AddFamilyMemberBody,
  CascadeRoleCode,
  ContactStatusCode,
  CreateFamilyBody,
  FamilyDto,
  FamilyExtensionsDto,
  FamilyMemberRecordDto,
  FamilySummaryDto,
  MemberSexCode,
  PageFamilyMemberRecordDto,
  PageFamilySummaryDto,
  PatchFamilyBody,
  PhysicalChartDto,
  UpdateFamilyMemberBody,
} from '@/features/families/api';

// admin slice types
export type {
  CreateUserRequestDto,
  PageUserDto,
  UserDto,
} from '@/features/admin-users/endpoints';

export type { PageStaffDto, StaffDto } from '@/features/staff/endpoints';

export type { UpdateUserRequestBody } from '@/features/user-profile/endpoints';

export type {
  IdentifierDefinitionDto,
  IdentifierDefinitionPageDto,
  IdentifierDefinitionTypeCode,
  IdentifierTypeDto,
} from '@/features/admin-identifier-types/endpoints';

export type {
  ConsentTypeActiveFilter,
  ConsentTypeDefDto,
  JsonPatchOp,
} from '@/features/admin-consent-types/endpoints';

export type {
  DocumentDeliveryMethodDto,
  DocumentDeliveryMethodId,
  QuestionnaireTemplateDto,
  QuestionnaireTemplatePageDto,
} from '@/features/admin-questionnaire-templates/endpoints';

export type {
  QuestionnaireStatusCode,
  QuestionnaireSummaryDto,
  QuestionnaireSummaryPageDto,
} from '@/features/browse-questionnaires/endpoints';

export type {
  ConfigDefinitionDto,
  ConfigExposure,
  ConfigItemDto,
  ConfigScope,
  ConfigSource,
  ConfigType,
  ListConfigsFilters,
  UpdateSystemConfigRequestDto,
} from '@/features/admin-configs/endpoints';

export type {
  FeatureFlagManifestEntryDto,
  FeatureFlagMapDto,
  FeatureFlagValue,
} from '@/features/feature-flags/endpoints';

export type {
  FhirIdentifierMappingRequest,
  FhirIdentifierMappingDto,
  FhirMappedFieldsDto,
  SystemAccountStatusDto,
  UpdateFhirIdentifierMappingRequest,
  UpdateMappedFieldsRequest,
  UpdateSystemAccountRequest,
} from '@/features/admin-fhir-integrations/types';

export type {
  DbPoolEntryDto,
  DbPoolStatsDto,
  DbProcessEntryDto,
  DbServerStatsDto,
  LoggerLevelDto,
  SetLoggerLevelRequestDto,
  SystemBuildInfoBuildBlockDto,
  SystemBuildInfoDto,
  SystemBuildInfoGitBlockDto,
  SystemBuildInfoInstanceDto,
  SystemBuildInfoServiceDto,
  SystemLoggersDto,
  SystemLoggersInstanceDto,
  SystemLoggersServiceDto,
} from '@/features/admin-system/api';

// platform slice types (PTV2-1021)
export type {
  ApplyTenantTemplateRequestDto,
  ClusterGrantCapability,
  ClusterGrantDto,
  ClusterGrantFilters,
  CreateClusterGrantRequestDto,
  CreateTenantRequestDto,
  CreateTenantTemplateRequestDto,
  DeploymentManagerHealthDto,
  DeploymentStatus,
  DockerHubCredentialsDto,
  ExitStatusDto,
  JobExecutionDto,
  JobLaunchDto,
  JobStatus,
  ListJobsParams,
  ListTenantUsersParams,
  OverridableServiceName,
  OverrideJobLaunchDto,
  ProvisionedUserDto,
  ProvisionTenantUserRequestDto,
  PutDockerHubCredentialsRequestDto,
  ReplaceTenantUserRolesRequestDto,
  ResourceUsageDto,
  SearchTenantUsersParams,
  ServiceOverrideRequestDto,
  TenantAdminUserDto,
  TenantAdminUserPageDto,
  TenantDto,
  TenantPageDto,
  TenantRoleDto,
  TenantServiceOverrideDto,
  TenantState,
  TenantTemplateDto,
  UpdateTenantRequestDto,
} from '@/features/platform/endpoints';

export const apiClient = {
  ...meApi,
  ...patientRecordApi,
  ...patientSearchApi,
  ...familiesApi,
  ...adminUsersEndpoints,
  ...userProfileEndpoints,
  ...adminIdentifierTypesEndpoints,
  ...adminConsentTypesEndpoints,
  ...adminQuestionnaireTemplatesEndpoints,
  ...browseQuestionnairesEndpoints,
  ...adminConfigsEndpoints,
  ...adminFhirIntegrationsEndpoints,
  ...fhirLauncherEndpoints,
  ...adminSystemEndpoints,
  ...featureFlagsEndpoints,
  ...platformEndpoints,
  ...staffEndpoints,
};
